import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import './Settings.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Settings() {
  const [settings, setSettings] = useState({
    logo_url: '',
    marquee_text: '',
    title: '',
    subtitle: '',
    whatsapp_link: '',
    telegram_link: '',
    official_site_url: '',
    show_whatsapp: 'true',
    show_telegram: 'true',
    download_url: '',
    bank_options: '',
    referral_options: '',
    form_comment: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: ''
  });
  const [mainBanners, setMainBanners] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, bannerId: null, bannerType: null });
  
  const logoInputRef = useRef(null);
  const mainBannerInputRef = useRef(null);
  const scrollingBannerInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const [settingsRes, mainBannersRes, bannersRes] = await Promise.all([
        fetch(`${API_URL}/settings`),
        fetch(`${API_URL}/main-banners`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/banners`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const settingsData = await settingsRes.json();
      const mainBannersData = await mainBannersRes.json();
      const bannersData = await bannersRes.json();
      
      setSettings(prev => ({ ...prev, ...settingsData }));
      setMainBanners(mainBannersData);
      setBanners(bannersData);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('admin_token');
    const formData = new FormData();
    formData.append('image', file);

    setUploading(prev => ({ ...prev, logo: true }));

    try {
      const response = await fetch(`${API_URL}/settings/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, logo_url: data.imageUrl }));
        toast.success('Logo uploaded successfully');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to upload image');
        console.error('Upload error:', errorData);
      }
    } catch (error) {
      toast.error('Upload failed: ' + error.message);
      console.error('Upload error:', error);
    } finally {
      setUploading(prev => ({ ...prev, logo: false }));
      e.target.value = '';
    }
  };

  const handleMainBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (mainBanners.length >= 10) {
      toast.error('Maximum 10 main banners allowed');
      return;
    }

    const token = localStorage.getItem('admin_token');
    const formData = new FormData();
    formData.append('image', file);
    formData.append('sort_order', mainBanners.length);

    setUploading(prev => ({ ...prev, mainBanner: true }));

    try {
      const response = await fetch(`${API_URL}/main-banners`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setMainBanners(prev => [...prev, data]);
        toast.success('Banner added successfully');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to add banner');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, mainBanner: false }));
      e.target.value = '';
    }
  };

  const handleScrollingBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (banners.length >= 10) {
      toast.error('Maximum 10 banners allowed');
      return;
    }

    const token = localStorage.getItem('admin_token');
    const formData = new FormData();
    formData.append('image', file);
    formData.append('sort_order', banners.length);

    setUploading(prev => ({ ...prev, scrollingBanner: true }));

    try {
      const response = await fetch(`${API_URL}/banners`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setBanners(prev => [...prev, data]);
        toast.success('Banner added successfully');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to add banner');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, scrollingBanner: false }));
      e.target.value = '';
    }
  };

  const openDeleteConfirm = (id, type) => {
    setConfirmModal({ isOpen: true, bannerId: id, bannerType: type });
  };

  const closeDeleteConfirm = () => {
    setConfirmModal({ isOpen: false, bannerId: null, bannerType: null });
  };

  const handleDeleteBanner = async () => {
    const { bannerId, bannerType } = confirmModal;
    if (!bannerId) return;

    const token = localStorage.getItem('admin_token');
    const endpoint = bannerType === 'main' ? 'main-banners' : 'banners';

    try {
      const response = await fetch(`${API_URL}/${endpoint}/${bannerId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        if (bannerType === 'main') {
          setMainBanners(prev => prev.filter(b => b.id !== bannerId));
        } else {
          setBanners(prev => prev.filter(b => b.id !== bannerId));
        }
        toast.success('Banner deleted');
      } else {
        toast.error('Failed to delete banner');
      }
    } catch (error) {
      toast.error('Delete failed');
    } finally {
      closeDeleteConfirm();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Settings saved successfully!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    // Remove /api from end of URL only
    const baseUrl = API_URL.endsWith('/api') 
      ? API_URL.slice(0, -4) 
      : API_URL.replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Delete Banner"
        message="Are you sure you want to delete this banner? This action cannot be undone."
        onConfirm={handleDeleteBanner}
        onCancel={closeDeleteConfirm}
      />
      
      <form onSubmit={handleSubmit}>
        
        {/* 1. Logo */}
        <section className="settings-section">
          <h2>1️⃣ Logo Image</h2>
          <div className="image-upload-row">
            <div className="image-preview-box">
              {settings.logo_url ? (
                <img src={getImageUrl(settings.logo_url)} alt="Logo" />
              ) : (
                <span className="no-image">No logo</span>
              )}
            </div>
            <div className="upload-controls">
              <input type="file" ref={logoInputRef} onChange={handleLogoUpload} accept="image/*" hidden />
              <button type="button" className="btn btn-secondary" onClick={() => logoInputRef.current.click()} disabled={uploading.logo}>
                {uploading.logo ? 'Uploading...' : '📤 Upload Logo'}
              </button>
              <input type="text" name="logo_url" value={settings.logo_url} onChange={handleChange} placeholder="Or enter image URL" />
            </div>
          </div>
        </section>

        {/* 2. Marquee Text */}
        <section className="settings-section">
          <h2>2️⃣ Marquee Running Text</h2>
          <div className="form-group">
            <input
              type="text"
              name="marquee_text"
              value={settings.marquee_text}
              onChange={handleChange}
              placeholder="🎉 Welcome! Register now and get exclusive bonuses! 🎁"
            />
          </div>
        </section>

        {/* 3. Main Banner Carousel (Max 10) */}
        <section className="settings-section">
          <h2>3️⃣ Main Banner Carousel (Max 10)</h2>
          <p className="section-desc">Images will auto-slide every 3 seconds with navigation arrows</p>
          <div className="main-banners-grid">
            {mainBanners.map((banner, index) => (
              <div key={banner.id} className="main-banner-item">
                <img src={getImageUrl(banner.image_url)} alt={`Main Banner ${index + 1}`} />
                <button type="button" className="delete-banner-btn" onClick={() => openDeleteConfirm(banner.id, 'main')}>✕</button>
                <span className="banner-number">{index + 1}</span>
              </div>
            ))}
            {mainBanners.length < 10 && (
              <div className="add-banner-box main">
                <input type="file" ref={mainBannerInputRef} onChange={handleMainBannerUpload} accept="image/*" hidden />
                <button
                  type="button"
                  className="add-banner-btn"
                  onClick={() => mainBannerInputRef.current.click()}
                  disabled={uploading.mainBanner}
                >
                  {uploading.mainBanner ? '...' : '+'}
                </button>
                <span>Add Banner</span>
              </div>
            )}
          </div>
          <small className="hint">{mainBanners.length}/10 banners</small>
        </section>

        {/* 4. Title */}
        <section className="settings-section">
          <h2>4️⃣ Title</h2>
          <div className="form-group">
            <input type="text" name="title" value={settings.title} onChange={handleChange} placeholder="a9playau" />
          </div>
        </section>

        {/* 5. Subtitle */}
        <section className="settings-section">
          <h2>5️⃣ Subtitle</h2>
          <div className="form-group">
            <input type="text" name="subtitle" value={settings.subtitle} onChange={handleChange} placeholder="Welcome Bonus 100%" />
          </div>
        </section>

        {/* 6. Form Options (Bank, Referral & Comment) */}
        <section className="settings-section">
          <h2>6️⃣ Form Options</h2>
          <div className="form-group">
            <label>Bank Options</label>
            <textarea
              name="bank_options"
              value={settings.bank_options}
              onChange={handleChange}
              placeholder="Maybank,CIMB,Public Bank,RHB"
              rows={3}
            />
            <small className="hint">Enter bank names separated by commas</small>
          </div>
          <div className="form-group">
            <label>Referral Source Options ("Where did you hear about us?")</label>
            <textarea
              name="referral_options"
              value={settings.referral_options}
              onChange={handleChange}
              placeholder="Facebook,Instagram,TikTok,WhatsApp,Telegram,Friend,Google,Other"
              rows={3}
            />
            <small className="hint">Enter options separated by commas</small>
          </div>
          <div className="form-group">
            <label>Form Comment (shown below form fields)</label>
            <input
              type="text"
              name="form_comment"
              value={settings.form_comment}
              onChange={handleChange}
              placeholder="* All fields entered must be real"
            />
          </div>
        </section>

        {/* 7. Social & Official Site Buttons */}
        <section className="settings-section">
          <h2>7️⃣ Social & Official Site Buttons</h2>
          <div className="form-row">
            <div className="form-group">
              <div className="checkbox-row">
                <input 
                  type="checkbox" 
                  id="show_whatsapp"
                  checked={settings.show_whatsapp === 'true'}
                  onChange={(e) => setSettings(prev => ({ ...prev, show_whatsapp: e.target.checked ? 'true' : 'false' }))}
                />
                <label htmlFor="show_whatsapp">Show WhatsApp Button</label>
              </div>
              <input type="url" name="whatsapp_link" value={settings.whatsapp_link} onChange={handleChange} placeholder="https://wa.me/60123456789" />
            </div>
            <div className="form-group">
              <div className="checkbox-row">
                <input 
                  type="checkbox" 
                  id="show_telegram"
                  checked={settings.show_telegram === 'true'}
                  onChange={(e) => setSettings(prev => ({ ...prev, show_telegram: e.target.checked ? 'true' : 'false' }))}
                />
                <label htmlFor="show_telegram">Show Telegram Button</label>
              </div>
              <input type="url" name="telegram_link" value={settings.telegram_link} onChange={handleChange} placeholder="https://t.me/yourchannel" />
            </div>
          </div>
          <div className="form-group">
            <label>Official Site URL</label>
            <input type="url" name="official_site_url" value={settings.official_site_url} onChange={handleChange} placeholder="https://yoursite.com" />
            <small className="hint">Button will appear if URL is provided</small>
          </div>
        </section>

        {/* 8. Download Button */}
        <section className="settings-section">
          <h2>8️⃣ Download App Button</h2>
          <div className="form-group">
            <label>Download App URL</label>
            <input type="url" name="download_url" value={settings.download_url} onChange={handleChange} placeholder="https://example.com/app.apk" />
            <small className="hint">Users will be redirected to this URL when clicking the Download button</small>
          </div>
        </section>

        {/* 9. Scrolling Banners */}
        <section className="settings-section">
          <h2>9️⃣ Scrolling Banners (Max 10)</h2>
          <div className="scrolling-banners-grid">
            {banners.map((banner, index) => (
              <div key={banner.id} className="scrolling-banner-item">
                <img src={getImageUrl(banner.image_url)} alt={`Banner ${index + 1}`} />
                <button type="button" className="delete-banner-btn" onClick={() => openDeleteConfirm(banner.id, 'scrolling')}>✕</button>
                <span className="banner-number">{index + 1}</span>
              </div>
            ))}
            {banners.length < 10 && (
              <div className="add-banner-box">
                <input type="file" ref={scrollingBannerInputRef} onChange={handleScrollingBannerUpload} accept="image/*" hidden />
                <button
                  type="button"
                  className="add-banner-btn"
                  onClick={() => scrollingBannerInputRef.current.click()}
                  disabled={uploading.scrollingBanner}
                >
                  {uploading.scrollingBanner ? '...' : '+'}
                </button>
                <span>Add Banner</span>
              </div>
            )}
          </div>
          <small className="hint">{banners.length}/10 banners</small>
        </section>

        {/* 10. SEO Meta Tags */}
        <section className="settings-section">
          <h2>🔟 SEO Meta Tags</h2>
          <p className="section-desc">Configure meta tags for search engine optimization</p>
          <div className="form-group">
            <label>Meta Title</label>
            <input
              type="text"
              name="meta_title"
              value={settings.meta_title}
              onChange={handleChange}
              placeholder="Page title for search engines"
            />
            <small className="hint">Recommended: 50-60 characters</small>
          </div>
          <div className="form-group">
            <label>Meta Description</label>
            <textarea
              name="meta_description"
              value={settings.meta_description}
              onChange={handleChange}
              placeholder="Brief description of your page for search results"
              rows={3}
            />
            <small className="hint">Recommended: 150-160 characters</small>
          </div>
          <div className="form-group">
            <label>Meta Keywords</label>
            <input
              type="text"
              name="meta_keywords"
              value={settings.meta_keywords}
              onChange={handleChange}
              placeholder="keyword1, keyword2, keyword3"
            />
            <small className="hint">Separate keywords with commas</small>
          </div>
        </section>

        {/* Save Button */}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary btn-save" disabled={saving}>
            {saving ? 'Saving...' : '💾 Save All Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Settings;
